import { RESOURCES_NAMES } from "@/const/resource.constants"
import {
  Config,
  GetUseMutationOptions,
  Params,
  Updater,
  useHttp,
  useLoadMore,
  useUpdate
} from "@/hooks/useHttp"

import type { ConfigLoadMore } from "@/hooks/useHttp.types"
import type {
  AdrIncidentResponse,
  AdrIncidentsResponse,
  AdrStateResponse,
  EnvironmentsResponse,
  IncidentHistoryParams,
  IncidentsHistoryResponse,
  PhaseResponse,
  RuleResponse
} from "./useIncidents.types"

export const useLoadMoreAdrIncidents = <T>(
  params: Params<Record<string, unknown>> = {},
  config?: ConfigLoadMore<T>
) => {
  const context = useLoadMore<T>(RESOURCES_NAMES.INCIDENTS.LIST, params, config)

  return context
}

export const useGetAdrIncidents = <T extends AdrIncidentsResponse, S = T>(
  params: Params,
  config?: Config<T, S>
) => {
  const context = useHttp<T, S>(RESOURCES_NAMES.INCIDENTS.LIST, params, config)

  return context
}

export const useGetAdrIncident = <T = AdrIncidentResponse, S = T>(
  incidentId: string,
  config?: Config<T, S>
) => {
  const context = useHttp<T, S>(
    `${RESOURCES_NAMES.INCIDENTS.DESCRIBE}/${incidentId}`,
    undefined,
    config
  )

  return context
}

export const useGetIncidentsHistory = <T = IncidentsHistoryResponse, S = T>(
  params: Params<IncidentHistoryParams> = {},
  config?: Config<T, S>
) => {
  const context = useHttp<T, S>(
    RESOURCES_NAMES.INCIDENTS.HISTORY,
    params,
    config
  )

  return context
}

export const useDismissIncident = <T = unknown, S = T>(
  params?: Params,
  updater?: Updater<T, S>,
  options?: GetUseMutationOptions<T, S>
) => {
  const context = useUpdate<T, S>(
    RESOURCES_NAMES.INCIDENTS.DISMISS,
    params,
    updater,
    options
  )

  return context
}

export const usePhase = <T = PhaseResponse, S = T>() => {
  const context = useHttp<T, S>(RESOURCES_NAMES.INCIDENTS.PHASE)

  return context
}

export const useGetRules = <T extends RuleResponse, S = T>(
  config?: Config<T, S>
) => {
  const context = useHttp<T, S>(
    RESOURCES_NAMES.INCIDENTS.RULES,
    undefined,
    config
  )

  return context
}

export const useGetEnvironments = <T extends EnvironmentsResponse, S = T>(
  config?: Config<T, S>
) => {
  const context = useHttp<T, S>(
    RESOURCES_NAMES.INCIDENTS.ENVIRONMENTS,
    undefined,
    config
  )

  return context
}

export const useGetAdrState = <T extends AdrStateResponse, S = T>(
  config?: Config<T, S>
) => {
  const context = useHttp<T, S>(
    RESOURCES_NAMES.INCIDENTS.ADR_STATE,
    undefined,
    config
  )

  return context
}
